import React, { useEffect, useState, lazy } from 'react';
import { KeyIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function ProductSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const images = [{
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4FNSFqQei2g9fS3DyYz7MU/ChatGPT_Image_11_oct_2025%2C_18_48_21.png',
    alt: 'Kit de Emergencia Anti-Estrés con 5 Bonos'
  }, {
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jenRCUvUKSzkShErgrJLR5/ChatGPT_Image_11_oct_2025%2C_18_47_25.png',
    alt: 'Kit de Emergencia Anti-Estrés'
  }];
  // Preload images after initial render
  useEffect(() => {
    // Defer image preloading to not block initial page load
    const timeoutId = setTimeout(() => {
      const imagePromises = images.map(image => {
        return new Promise(resolve => {
          const img = new Image();
          img.src = image.src;
          img.onload = resolve;
          img.onerror = resolve; // Resolve even on error to not block
        });
      });
      Promise.all(imagePromises).then(() => {
        setImagesLoaded(true);
      });
    }, 1500); // Delay preloading by 1.5 seconds
    return () => clearTimeout(timeoutId);
  }, []);
  const nextSlide = () => {
    setCurrentSlide(prev => prev === images.length - 1 ? 0 : prev + 1);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? images.length - 1 : prev - 1);
  };
  const goToSlide = index => {
    setCurrentSlide(index);
  };
  return <section className="py-16 bg-white" data-id="element-246">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
        <div className="text-center mb-12" data-id="element-248">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-249">
            ✅ La Solución: Tu Kit de Emergencia Anti-Estrés
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-250">
            Un eBook práctico y rápido de aplicar. Diseñado para ser tu
            protocolo de calma instantánea.
          </p>
          <p className="mt-2 text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-251">
            Técnicas simples, efectivas y respaldadas por ciencia para recuperar
            tu paz mental en minutos, sin importar dónde estés.
          </p>
        </div>
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center" data-id="element-252">
          {/* Carousel container */}
          <div className="relative overflow-hidden rounded-lg shadow-lg mb-10 lg:mb-0" data-id="element-253">
            {/* Images */}
            <div className="relative h-80 sm:h-96 w-full" data-id="element-254">
              {images.map((image, index) => <div key={index} className={`absolute inset-0 transition-opacity duration-500 ${currentSlide === index ? 'opacity-100' : 'opacity-0'}`} data-id="element-255">
                  <img src={image.src} alt={image.alt} className="w-full h-full object-contain" loading={index === 0 ? 'eager' : 'lazy'} data-id="element-256" />
                </div>)}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center" data-id="element-257">
                <div className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium" data-id="element-258">
                  60 páginas
                </div>
              </div>
            </div>
            {/* Navigation arrows */}
            <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm p-2 rounded-full shadow hover:bg-white transition-colors" aria-label="Previous slide" data-id="element-259">
              <ChevronLeftIcon className="h-5 w-5 text-gray-700" data-id="element-260" />
            </button>
            <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm p-2 rounded-full shadow hover:bg-white transition-colors" aria-label="Next slide" data-id="element-261">
              <ChevronRightIcon className="h-5 w-5 text-gray-700" data-id="element-262" />
            </button>
            {/* Indicators */}
            <div className="absolute bottom-4 left-0 right-0" data-id="element-263">
              <div className="flex justify-center gap-2" data-id="element-264">
                {images.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-2 w-2 rounded-full ${currentSlide === index ? 'bg-green-600' : 'bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-265" />)}
              </div>
            </div>
          </div>
          <div data-id="element-266">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-267">
              📍 ¿Cuándo lo puedes usar?
            </h3>
            <ul className="space-y-4 mb-8" data-id="element-268">
              <li className="flex items-start" data-id="element-269">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-270" />
                <p className="text-gray-700" data-id="element-271">
                  En el trabajo, antes de una reunión o entrega importante.
                </p>
              </li>
              <li className="flex items-start" data-id="element-272">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-273" />
                <p className="text-gray-700" data-id="element-274">
                  En casa, cuando la tensión familiar te sobrepasa.
                </p>
              </li>
              <li className="flex items-start" data-id="element-275">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-276" />
                <p className="text-gray-700" data-id="element-277">
                  En la noche, cuando tu mente no te deja dormir.
                </p>
              </li>
              <li className="flex items-start" data-id="element-278">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-279" />
                <p className="text-gray-700" data-id="element-280">
                  En el transporte, cuando la ansiedad aparece de golpe.
                </p>
              </li>
            </ul>
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-281">
              📘 ¿Qué vas a encontrar adentro?
            </h3>
            <ul className="space-y-4" data-id="element-282">
              <li className="flex items-start" data-id="element-283">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-284" />
                <p className="text-gray-700" data-id="element-285">
                  Los 3 errores que agravan tu estrés sin que te des cuenta.
                </p>
              </li>
              <li className="flex items-start" data-id="element-286">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-287" />
                <p className="text-gray-700" data-id="element-288">
                  La Respiración Cuadrada usada por los Navy SEALs para calmar
                  el sistema nervioso.
                </p>
              </li>
              <li className="flex items-start" data-id="element-289">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-290" />
                <p className="text-gray-700" data-id="element-291">
                  El Método 5-4-3-2-1 para frenar pensamientos acelerados.
                </p>
              </li>
              <li className="flex items-start" data-id="element-292">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-293" />
                <p className="text-gray-700" data-id="element-294">
                  Un Ritual Exprés de 5 Minutos para resetearte.
                </p>
              </li>
              <li className="flex items-start" data-id="element-295">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-296" />
                <p className="text-gray-700" data-id="element-297">
                  Un Checklist Anti-Estrés para tener siempre tu kit listo.
                </p>
              </li>
              <li className="flex items-start" data-id="element-298">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-299" />
                <p className="text-gray-700" data-id="element-300">
                  Una rutina diaria de 10 minutos para blindarte frente a la
                  ansiedad.
                </p>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-16" data-id="element-301">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-302">
            🎁 Además: 5 BONOS GRATIS incluidos
          </h3>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-303">
            <div className="bg-amber-50 p-5 rounded-lg" data-id="element-304">
              <div className="text-amber-800 font-bold mb-2" data-id="element-305">
                1. Respiraciones para Dormir Mejor
              </div>
              <p className="text-sm text-gray-700" data-id="element-306">
                5 técnicas fáciles para conciliar el sueño naturalmente.
              </p>
            </div>
            <div className="bg-amber-50 p-5 rounded-lg" data-id="element-307">
              <div className="text-amber-800 font-bold mb-2" data-id="element-308">
                2. Ritual Exprés para Calmarte
              </div>
              <p className="text-sm text-gray-700" data-id="element-309">
                Un protocolo de 5 minutos para bajar la ansiedad al instante.
              </p>
            </div>
            <div className="bg-amber-50 p-5 rounded-lg" data-id="element-310">
              <div className="text-amber-800 font-bold mb-2" data-id="element-311">
                3. Diario Anti-Estrés de 7 Días
              </div>
              <p className="text-sm text-gray-700" data-id="element-312">
                Plantillas listas para soltar preocupaciones y liberar tu mente.
              </p>
            </div>
            <div className="bg-amber-50 p-5 rounded-lg" data-id="element-313">
              <div className="text-amber-800 font-bold mb-2" data-id="element-314">
                4. Playlist Secreta Anti-Estrés
              </div>
              <p className="text-sm text-gray-700" data-id="element-315">
                Música relajante y sonidos binaurales para restaurar tu
                equilibrio.
              </p>
            </div>
            <div className="bg-amber-50 p-5 rounded-lg" data-id="element-316">
              <div className="text-amber-800 font-bold mb-2" data-id="element-317">
                5. Afirmaciones Calmantes de Poder
              </div>
              <p className="text-sm text-gray-700" data-id="element-318">
                Frases transformadoras para reprogramar tu mente hacia la calma.
              </p>
            </div>
          </div>
          <p className="text-center text-gray-500 mt-4" data-id="element-319">
            (Valor real: más de $50, incluidos hoy totalmente GRATIS)
          </p>
        </div>
        <div className="mt-16 text-center" data-id="element-320">
          <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-321">
            🌙 Imagina esto…
          </h3>
          <div className="max-w-3xl mx-auto" data-id="element-322">
            <ul className="text-left space-y-2" data-id="element-323">
              <li className="flex items-start" data-id="element-324">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-325" />
                <p className="text-gray-700" data-id="element-326">
                  Poder cerrar los ojos 3 minutos en medio del caos y volver con
                  la mente clara.
                </p>
              </li>
              <li className="flex items-start" data-id="element-327">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-328" />
                <p className="text-gray-700" data-id="element-329">
                  Dormir profundamente sin pastillas ni químicos.
                </p>
              </li>
              <li className="flex items-start" data-id="element-330">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-331" />
                <p className="text-gray-700" data-id="element-332">
                  Manejar situaciones estresantes con calma y confianza.
                </p>
              </li>
              <li className="flex items-start" data-id="element-333">
                <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-334" />
                <p className="text-gray-700" data-id="element-335">
                  Tener siempre un recurso rápido para no dejar que la ansiedad
                  te controle.
                </p>
              </li>
            </ul>
          </div>
          <div className="mt-10 bg-green-50 rounded-xl p-6 inline-block" data-id="element-336">
            <div className="flex items-center justify-center" data-id="element-337">
              <KeyIcon className="h-6 w-6 text-green-600 mr-2" data-id="element-338" />
              <h4 className="text-xl font-bold text-gray-900" data-id="element-339">
                Garantía Cero Riesgo
              </h4>
            </div>
            <p className="mt-2 text-gray-600" data-id="element-340">
              Pruébalo durante 7 días. Si no sientes que te ayuda, te devolvemos
              tu dinero. Sin preguntas. Sin complicaciones.
            </p>
          </div>
        </div>
        <div className="mt-12 text-center" data-id="element-341">
          <p className="text-xl font-medium text-gray-900 mb-4" data-id="element-342">
            🚀 Tu mente merece un respiro.
          </p>
          <p className="text-lg text-gray-700" data-id="element-343">
            Haz clic ahora y accede al Kit de Emergencia Anti-Estrés con todos
            los bonos incluidos.
          </p>
          <div className="mt-6" data-id="element-344">
            <a href="https://clickylee.com/products/kit-de-emergencias-anti-stress-5-bonos-gratis?variant=46954999415019" className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md shadow-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors" data-id="element-345">
              Quiero mi Kit de Emergencia Anti-Estrés Ahora
            </a>
            <p className="text-sm text-gray-500 mt-2" data-id="element-346">
              Acceso inmediato desde tu celular o computadora
            </p>
          </div>
        </div>
      </div>
    </section>;
}